/* eslint-disable unicorn/consistent-function-scoping */
import { Badge, Box, Button, Card, Divider, Flex, Group, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useMemo } from "react";
import EstrategiaItem from "./EstrategiaItem";
import useGetPergunta from "./useGetPerguntas";
import useQueryRespostas from "./useGetRespostas";
import useQueryRespostasEmpresa from "./useGetRespostasEmpresa";
import useGetStatusIdEmpresa from "./useGetStatus";
import useMediaGrupoByEmpresaId from "../cards/useGetMediaGrupoByEmpresaId";

type CardHeaderProperties = {
  grupo: TGruposDiagnostico;
  opened: boolean;
  toggle: () => void;
};

/**
 * CardHeader
 * @param root0 - properties
 * @param root0.grupo - grupo
 * @param root0.opened - opened
 * @param root0.toggle - toggle
 * @returns CardHeader
 */
function CardHeader({ grupo, opened, toggle }: CardHeaderProperties) {
  return (
    <Group align="center" justify="space-between">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Stack align="flex-start">
          <Text size="xl" lineClamp={1}>
            {grupo.nome}
          </Text>
          <Text size="sm" lineClamp={1}>
            {grupo.descricao}
          </Text>
        </Stack>
      </Box>
      <Button
        type="button"
        variant="transparent"
        size="compact-sm"
        rightSection={opened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
        onClick={toggle}
        style={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        Ver mais
      </Button>
    </Group>
  );
}

const EstrategiaList = ({
  grupo,
  empresaProjetoId
}: {
  grupo: TGruposDiagnostico;
  empresaProjetoId?: string | undefined;
}) => {
  const [opened, { toggle }] = useDisclosure(!!empresaProjetoId);
  const { data: statusArray } = useGetStatusIdEmpresa();
  const { data } = useGetPergunta({ ids: [grupo?.id] });
  // const totalPerguntas = data?.length || 1;

  const { data: respostas } = useQueryRespostas({ id: grupo?.idQuestionario, empresaProjetoId: empresaProjetoId });
  const { data: respostasEmpresas } = useQueryRespostasEmpresa({
    id: grupo?.idQuestionario,
    empresaProjetoId: empresaProjetoId
  });
  const { data: mediaGrupos } = useMediaGrupoByEmpresaId({
    id: grupo?.idQuestionario || "",
    empresaProjetoId: empresaProjetoId
  });

  //let calculoMedia = 0;
  // const hasMediaAvaliador = mediaGrupos?.some((item) => item && item.mediaAvaliador !== undefined);

  // const media = useMemo(() => {
  //   if (!mediaGrupos || mediaGrupos.length === 0) {
  //     return 0;
  //   }
  //   const hasAvaliador = mediaGrupos.some((item) => item.mediaAvaliador !== undefined);
  //   let soma = 0;
  //   for (const item of mediaGrupos) {
  //     const valor = hasAvaliador ? item.mediaAvaliador : item.media;
  //     soma += valor ?? 0;
  //   }
  //   return soma / mediaGrupos.length;
  // }, [mediaGrupos, totalPerguntas]);

  const feedback = useMemo(() => {
    const grupoMedia = mediaGrupos?.find((item) => item.grupoId === grupo.id);
    const mediaDoGrupo = grupoMedia ? (grupoMedia.mediaAvaliador ?? grupoMedia.media) : 0;
    return grupo.feedbacks.find((f) => f.start <= mediaDoGrupo && Math.floor(mediaDoGrupo) <= f.end);
  }, [grupo, mediaGrupos]);

  if (!respostas || !respostasEmpresas || !opened) {
    return (
      <Card withBorder shadow="md" style={{ flex: 1, marginBottom: 15 }}>
        <CardHeader grupo={grupo} opened={opened} toggle={toggle} />
      </Card>
    );
  }

  const perguntas = data?.sort((a, b) => a?.ordem - b?.ordem) || [];
  const statusItem = statusArray && statusArray[0];
  const respostasItem = [...respostas, ...respostasEmpresas];

  return (
    <Card withBorder shadow="md" style={{ flex: 1, marginBottom: 15 }}>
      <CardHeader grupo={grupo} opened={opened} toggle={toggle} />

      <Divider my="xs" />

      <Stack>
        <Group justify="space-between" align="center">
          <Text size="lg">Análise do resultado na dimensão</Text>

          <Group>
            {grupo.feedbacks.map((f) => {
              const grupoMedia = mediaGrupos?.find((item) => item.grupoId === grupo.id);
              const mediaDoGrupo = grupoMedia ? (grupoMedia.mediaAvaliador ?? grupoMedia.media) : 0;
              const isSelected = f.start <= mediaDoGrupo && Math.floor(mediaDoGrupo) <= f.end;
              return (
                <Badge key={f.title} variant={isSelected ? "filled" : "transparent"}>
                  {f.title}
                </Badge>
              );
            })}
          </Group>
        </Group>
        <Text size="sm" fw="bold">
          {feedback?.description}
        </Text>
      </Stack>

      <Divider my="xs" />
      {!empresaProjetoId && (
        <Flex direction="column">
          {perguntas.length > 0 ? (
            perguntas.map((pergunta: TPergunta, index: number) => (
              <EstrategiaItem
                key={pergunta.id}
                pergunta={pergunta}
                numero={index + 1}
                statusItem={statusItem}
                respostas={respostasItem}
                grupo={grupo}
              />
            ))
          ) : (
            <Group justify="center">
              <Text fw="bold">Nenhum valor adicionado!</Text>
            </Group>
          )}
        </Flex>
      )}
    </Card>
  );
};

export default EstrategiaList;
